/**
 * actionTypeVocabulary — the one shared vocabulary for an action token, the field that names *what*
 * an employee did or proposes to do.
 *
 * Ten surfaces print an action token to the same technician, each with its own hand-kept map and each
 * rendering `map[token] ?? token`. Held per surface, a map is only ever as complete as the day it was
 * written, so a token a map lacks is not an unlabelled corner case — it is the producer's own English
 * snake_case identifier inside a Hebrew audit row, which is the opposite of what
 * `primary_response_is_human_readable_not_raw_runtime_log` asks for. That is what happened:
 * `AgentRunDetailPage` carried twelve keys and printed `browser_work_execute` and five `eset_*` types
 * raw on the run it was describing. One map here, free of JSX and of any data access, is what makes
 * that impossible — the same reason `statusVocabulary` and `evidenceSourceVocabulary` exist.
 *
 * The key set is every token a producer mints onto one of the three fields these surfaces label:
 *
 * 1. `AgentRun.analysis["proposed_action"]["action_type"]` and `ApprovalToken.action_type`:
 *    `action_plan_validation.ACTION_TYPES` (the four proposed actions, parity-pinned to
 *    `direct_llm_analyst._VALID_PROPOSED_ACTION_TYPES` and to `action_executor._SUPPORTED_ACTIONS`);
 *    `browser_work_executor.EXECUTION_ACTION_TYPE` (`browser_work_execute`, minted onto both fields
 *    by `approve_browser_work_execution`, reached from `POST /agents/{id}/browser-work-requests/
 *    {request_id}/approval`); and `approval_service.REMOTE_SESSION_ACTION_TYPE`
 *    (`run_diagnostic_script`, minted onto both by `mint_operator_remote_session_approval`).
 * 2. `AgentAction.action_type` — the run timeline: `initial_analysis` and `teamviewer_*`
 *    (`manager_agent`), `connection_active` (`agent_runs.py`), `check_executable`
 *    (`check_executable._TOOL_ID`), and the ESET read tools `manager_agent` records.
 * 3. The registry tokens the surfaces already labelled (`run_script`, `restart_service`,
 *    `reset_password`, `install_software`, `endpoint_diagnosis`). Kept so this map is a superset of
 *    every map it replaces; a token that no longer has a producer costs nothing here.
 *
 * `actionTypeVocabulary.test.js` reads the backend producer modules and fails if this map drifts from
 * any of them.
 *
 * Deliberately NOT a key: the compact chip abbreviations on the ticket list (`TicketsPage`) render
 * `pending_ai_action_type` as `סגור` / `מייל` / `הערה` inside a 9px badge and are a different claim
 * from a row's action name, and `ApprovalDecisionBrief` spells the four proposed actions out at
 * length because its whole job is explaining one decision — "הסלמה — העלאת עדיפות לגבוהה" is the
 * effect sentence, not this token's name.
 */
export const ACTION_TYPE_LABEL = {
  // ── The four proposed/executed actions ──────────────────────────────────────
  add_comment: "הוספת הערה",
  close_ticket: "סגירת קריאה",
  escalate: "העברה לגורם אחר",
  send_customer_message: "מסר ללקוח",

  // ── Run timeline ────────────────────────────────────────────────────────────
  initial_analysis: "ניתוח ראשוני",
  teamviewer_device_lookup: "TV — חיפוש מכשיר",
  teamviewer_session_create: "TV — יצירת חיבור",
  connection_active: "סימון חיבור פעיל",
  check_executable: "בדיקת קיום תוכנה בתחנה",
  endpoint_diagnosis: "אבחון תחנה",
  // The same words `EsetSpecialistPage`'s ESET_READ_LABELS uses for the same tools, so one ESET read
  // reads the same on the run timeline as on the ESET specialist screen.
  eset_device_security_lookup: "מצב אבטחה של מכשיר",
  eset_list_device_groups: "רשימת קבוצות מכשירים",
  eset_list_devices: "רשימת מכשירים",
  eset_list_devices_in_group: "מכשירים בקבוצה",
  eset_get_device_details: "פרטי מכשיר",

  // ── Bounded work through an approved tool ───────────────────────────────────
  // The session the operator approves and then watches on Remote Operations; the wording names the
  // bound, because the bound is the whole of what was approved.
  browser_work_execute: "ביצוע עבודת דפדפן תחומה",
  run_diagnostic_script: "הרצת סקריפט אבחון מאושר",

  // ── Registry tokens the surfaces already carried ────────────────────────────
  run_script: "הרצת סקריפט",
  restart_service: "איתחול שירות",
  reset_password: "איפוס סיסמה",
  install_software: "התקנת תוכנה",
};

/**
 * The Hebrew name of one action token, or the token itself when this build has no name for it.
 *
 * Returning the token is deliberate: an unmapped token is a token nobody has swept yet, and showing
 * the operator the producer's own identifier is the honest failure — inventing a Hebrew word for an
 * action the vocabulary does not describe would be a guess presented as a label.
 */
export function actionTypeLabel(token) {
  if (typeof token !== "string" || !token) return null;
  return ACTION_TYPE_LABEL[token] ?? token;
}
